import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { canUseStac } from 'src/app/services/stac-session.service';
import { StacConversionService, StacSceneLabel, StacTaskView } from 'src/app/services/stac-conversion.service';

/**
 * A scene's "Add to STAC" status on My Collections (plans/stac-conversion-eetaskapi.md §8.2), after
 * the scene's name: an amber or red one (a conversion to retry, or one that failed) is an icon with
 * its words on hover; the others are a small chip, with the rest in a tooltip.
 *
 * It only listens to the task's shared state (StacConversionService), which the task's "Add to
 * STAC" button reads, so a page of scenes costs no requests of its own. Nothing shows for a user
 * without STAC, before the task's state is known, or for a scene that hasn't been added to STAC,
 * whatever the reason: its sensor not switched on yet, ordered to the customer's own storage, or
 * archived — that one the Data Catalog banner says to restore (StacConversionComponent).
 *
 * Gated like the tags row (scene-tags.component.ts): `canUseStac` on `authService.user`,
 * re-checked on every emission, so an account that has just signed out hands nothing on.
 */
@Component({
  selector: 'app-stac-scene-status',
  templateUrl: './stac-scene-status.component.html',
  styleUrls: ['./stac-scene-status.component.scss'],
})
export class StacSceneStatusComponent implements OnInit, OnChanges, OnDestroy {
  /** The icon for each tone that shows as one; a tone not here shows as a chip. */
  static readonly ICONS: Readonly<Partial<Record<StacSceneLabel['tone'], string>>> = Object.freeze({
    attention: 'warning',
    failed: 'error',
  });

  @Input() taskId!: string;
  /** The sensor's uuid: `sensor.id` on the collections page's rebuilt objects. */
  @Input() sensorId!: string;
  @Input() scene: any;

  label: StacSceneLabel | null = null;
  /** The label's icon, or null for a chip. */
  icon: string | null = null;

  private eligible = false;
  private view: StacTaskView;
  private listeningTo: string | null = null;
  private userSub?: Subscription;
  private viewSub?: Subscription;

  constructor(private stac: StacConversionService, private authService: AuthService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.userSub = this.authService.user.subscribe((user) => {
      this.eligible = canUseStac(user);
      this.listen();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['taskId'] && this.listeningTo !== null && this.listeningTo !== this.taskId) this.listen();
    else this.update();
  }

  ngOnDestroy(): void {
    // One instance per scene row, so a leaked subscription here leaks per row, not per page.
    this.userSub?.unsubscribe();
    this.viewSub?.unsubscribe();
  }

  private listen(): void {
    this.viewSub?.unsubscribe();
    this.viewSub = undefined;
    this.view = undefined;
    this.listeningTo = null;
    if (this.eligible && this.taskId) {
      this.listeningTo = this.taskId;
      this.viewSub = this.stac.view$(this.taskId).subscribe((view) => {
        this.view = view;
        this.update();
      });
    }
    this.update();
  }

  private update(): void {
    const key = StacConversionService.sceneKey(this.sensorId, this.scene);
    this.label = this.view && key ? StacConversionService.sceneLabel(this.view.scenes.get(key)) : null;
    this.icon = (this.label && StacSceneStatusComponent.ICONS[this.label.tone]) || null;
    this.cdr.markForCheck();
  }
}
